import { supabase } from "@/integrations/supabase/client";
import { Conversation } from "./types";

/**
 * Get all conversations for the current user with optimized performance
 */
export const getConversations = async (): Promise<Conversation[]> => {
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user) {
    return [];
  }

  const { data: userProfile } = await supabase
    .from("profiles")
    .select("id, role")
    .eq("id", user.user.id)
    .single();

  if (!userProfile) {
    console.error("❌ [getConversations] Perfil do usuário não encontrado");
    return [];
  }

  // Query differently based on user role
  const isUserMentor = userProfile.role === 'mentor';
  const userColumnFilter = isUserMentor ? 'mentor_profile_id' : 'player_profile_id';

  // First get the conversations with pagination
  const { data: conversationsData, error } = await supabase
    .from("chat_conversations")
    .select("*")
    .eq(userColumnFilter, user.user.id)
    .order('last_message_at', { ascending: false })
    .limit(50); // Limit to 50 most recent conversations

  if (error) {
    console.error("❌ [getConversations] Erro ao buscar conversas:", error);
    throw error;
  }

  if (!conversationsData || conversationsData.length === 0) {
    return [];
  }

  // Get unread counts efficiently
  const conversationIds = conversationsData.map(c => c.id);
  const { data: unreadData } = await supabase
    .from("chat_messages")
    .select("conversation_id")
    .in("conversation_id", conversationIds)
    .eq("is_read", false)
    .neq("sender_profile_id", user.user.id);

  // Count unread messages per conversation
  const unreadCounts = new Map();
  if (unreadData) {
    unreadData.forEach(msg => {
      const count = unreadCounts.get(msg.conversation_id) || 0;
      unreadCounts.set(msg.conversation_id, count + 1);
    });
  }

  // Get profile data efficiently
  const mentorIds = [...new Set(conversationsData.map(c => c.mentor_profile_id).filter(Boolean))];
  const playerIds = [...new Set(conversationsData.map(c => c.player_profile_id).filter(Boolean))];

  // Fetch profiles in parallel
  const [mentorProfiles, playerProfiles] = await Promise.all([
    mentorIds.length > 0 ? supabase
      .from('profiles')
      .select('id, nickname, preferred_game, role')
      .in('id', mentorIds) : Promise.resolve({ data: [] }),
    playerIds.length > 0 ? supabase
      .from('profiles')
      .select('id, nickname, preferred_game, role')
      .in('id', playerIds) : Promise.resolve({ data: [] })
  ]);

  // Create lookup maps for efficient access
  const mentorLookup = new Map();
  const playerLookup = new Map();

  if (mentorProfiles.data) {
    mentorProfiles.data.forEach(profile => {
      mentorLookup.set(profile.id, profile);
    });
  }

  if (playerProfiles.data) {
    playerProfiles.data.forEach(profile => {
      playerLookup.set(profile.id, profile);
    });
  }

  // Build final conversations array
  const finalConversations: Conversation[] = conversationsData.map(conv => {
    const mentorProfile = mentorLookup.get(conv.mentor_profile_id);
    const playerProfile = playerLookup.get(conv.player_profile_id);

    return {
      id: conv.id,
      mentor_id: conv.mentor_profile_id,
      player_id: conv.player_profile_id,
      mentor_name: mentorProfile?.nickname || "Mentor",
      player_name: playerProfile?.nickname || "Jogador",
      last_message: (conv as any).last_message || "",
      is_active: (conv as any).is_active ?? true,
      last_message_at: conv.last_message_at,
      unread_count: unreadCounts.get(conv.id) || 0,
      created_at: conv.created_at,
      updated_at: conv.updated_at
    };
  });

  return finalConversations;
};

/**
 * Get a specific conversation by ID with complete details
 */
export const getConversationById = async (conversationId: string): Promise<Conversation | null> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    
    if (!user.user) {
      return null;
    }

    // Get the conversation
    const { data: conversation, error } = await supabase
      .from("chat_conversations")
      .select("*")
      .eq("id", conversationId)
      .single();

    if (error || !conversation) {
      console.error("❌ [getConversationById] Conversa não encontrada:", error);
      return null;
    }

    // Get profiles for both participants
    const [mentorProfile, playerProfile] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, nickname, preferred_game, role')
        .eq('id', conversation.mentor_profile_id)
        .single(),
      supabase
        .from('profiles')
        .select('id, nickname, preferred_game, role')
        .eq('id', conversation.player_profile_id)
        .single()
    ]);

    // Get unread count
    const { data: unreadData } = await supabase
      .from("chat_messages")
      .select("id")
      .eq("conversation_id", conversationId)
      .eq("is_read", false)
      .neq("sender_profile_id", user.user.id);

    const unreadCount = unreadData?.length || 0;

    return {
      id: conversation.id,
      mentor_id: conversation.mentor_profile_id,
      player_id: conversation.player_profile_id,
      mentor_name: mentorProfile.data?.nickname || "Mentor",
      player_name: playerProfile.data?.nickname || "Jogador",
      last_message: (conversation as any).last_message || "",
      is_active: (conversation as any).is_active ?? true,
      last_message_at: conversation.last_message_at,
      unread_count: unreadCount,
      created_at: conversation.created_at,
      updated_at: conversation.updated_at
    };

  } catch (error) {
    console.error("❌ [getConversationById] Erro:", error);
    return null;
  }
};

/**
 * Create or get existing conversation between users
 */
export const getOrCreateConversation = async (targetUserId: string): Promise<string> => {
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user) {
    throw new Error("User not authenticated");
  }

  // Get both user profiles to determine roles
  const [currentUserProfile, targetUserProfile] = await Promise.all([
    supabase.from("profiles").select("id, role").eq("id", user.user.id).single(),
    supabase.from("profiles").select("id, role").eq("id", targetUserId).single()
  ]);

  if (!currentUserProfile.data || !targetUserProfile.data) {
    throw new Error("User profiles not found");
  }

  const currentRole = currentUserProfile.data.role;
  const targetRole = targetUserProfile.data.role;

  // Determine mentor and player IDs based on roles
  let mentorId: string;
  let playerId: string;

  if (currentRole === 'mentor' && targetRole === 'mentor') {
    // Both are mentors - use target as mentor, current as "player" for consistency
    mentorId = targetUserId;
    playerId = user.user.id;
  } else if (currentRole === 'mentor') {
    // Current user is mentor
    mentorId = user.user.id;
    playerId = targetUserId;
  } else {
    // Current user is player/jogador
    mentorId = targetUserId;
    playerId = user.user.id;
  }

  // Check if conversation already exists (bidirectional search)
  const { data: existingConversation } = await supabase
    .from("chat_conversations")
    .select("id")
    .or(`and(mentor_profile_id.eq.${mentorId},player_profile_id.eq.${playerId}),and(mentor_profile_id.eq.${playerId},player_profile_id.eq.${mentorId})`)
    .single();

  if (existingConversation) {
    return existingConversation.id;
  }

  // Create new conversation
  const { data: newConversation, error } = await supabase
    .from("chat_conversations")
    .insert({
      mentor_profile_id: mentorId,
      player_profile_id: playerId
    })
    .select("id")
    .single();

  if (error) {
    console.error("❌ [getOrCreateConversation] Erro ao criar conversa:", error);
    throw error;
  }

  return newConversation.id;
};
